/* Amplitude-backed feature flags for Tokenwise (OPSS-55).
 *
 * Mirrors studio-web's Amplitude Experiment config (there inside @ai21/experiments):
 * bind to the already-loaded Amplitude Analytics instance so the device/user id
 * line up, point at the same-origin Caddy proxy, and read flags client-side. */

import {
  Experiment,
  type ExperimentClient,
} from "@amplitude/experiment-js-client";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { FunctionComponent, PropsWithChildren } from "react";

import { useCurrentUser } from "../../identity/client";
import {
  AMPLITUDE_EXPERIMENT_API_KEY,
  AMPLITUDE_EXPERIMENT_FLAGS_SERVER_URL,
  AMPLITUDE_EXPERIMENT_SERVER_URL,
  EMAIL_PASSWORD_AUTH_FLAG,
  FLAGS_DEBUG,
} from "./flags.config";
import type {
  EmailPasswordAuthState,
  FlagsState,
  IFlagsService,
} from "./flags.types";

/* One Experiment client for the app. Config matches studio-web's wrapper:
 * fetchOnStart/pollOnStart off (we fetch explicitly for the signed-in user),
 * auto-fetch on Amplitude identity change to keep variants fresh, 10s timeout. */
let client: ExperimentClient | undefined;
function getClient(): ExperimentClient {
  if (!client) {
    client = Experiment.initializeWithAmplitudeAnalytics(
      AMPLITUDE_EXPERIMENT_API_KEY ?? "",
      {
        automaticExposureTracking: true,
        automaticFetchOnAmplitudeIdentityChange: true,
        debug: FLAGS_DEBUG,
        fetchOnStart: false,
        fetchTimeoutMillis: 10000,
        pollOnStart: false,
        serverUrl: AMPLITUDE_EXPERIMENT_SERVER_URL,
        flagsServerUrl: AMPLITUDE_EXPERIMENT_FLAGS_SERVER_URL,
      },
    );
  }

  return client;
}

const FlagsContext = createContext<FlagsState>({
  ready: false,
  isEnabled: () => false,
});

const FlagsProvider: FunctionComponent<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { id, email } = useCurrentUser();
  const [ready, setReady] = useState(false);
  // Bumped on each settled fetch so `isEnabled` is re-derived from fresh variants.
  const [fetchToken, setFetchToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const experiment = getClient();

    (async () => {
      try {
        await experiment.start();
        // Fetch explicitly for the signed-in user (deterministic first load);
        // auto-fetch on identity change keeps it fresh afterwards.
        await experiment.fetch(
          id
            ? { user_id: id, user_properties: email ? { email } : undefined }
            : undefined,
        );
      } catch (error) {
        // Fail safe: leave variants empty ⇒ every flag reads off ⇒ UI hidden.
        console.error("[Flags] Amplitude Experiment fetch failed", error);
      } finally {
        if (!cancelled) {
          setReady(true);
          setFetchToken((t) => t + 1);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, email]);

  const value = useMemo<FlagsState>(
    () => ({
      ready,
      isEnabled: (flagKey: string) =>
        getClient().variant(flagKey)?.value === "on",
    }),
    // fetchToken invalidates the memo after each settled fetch so consumers
    // re-read `variant()` (which the SDK updates in place).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready, fetchToken],
  );

  return (
    <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>
  );
};

const useFlags = (): FlagsState => useContext(FlagsContext);

/* Anonymous flag read for the public auth screens (outside FlagsProvider and
 * identity). Fetches variants for the current device only; once the user signs
 * in, automaticFetchOnAmplitudeIdentityChange re-fetches on the same shared
 * client with their id. A failed fetch resolves to disabled (fail-safe: the
 * Google path is the product default; email/password is the debugging path). */
const useEmailPasswordAuth = (): EmailPasswordAuthState => {
  const [state, setState] = useState<EmailPasswordAuthState>({
    ready: false,
    enabled: false,
  });

  useEffect(() => {
    let cancelled = false;
    const experiment = getClient();

    (async () => {
      try {
        await experiment.start();
        await experiment.fetch();
      } catch (error) {
        console.error("[Flags] anonymous Experiment fetch failed", error);
      } finally {
        if (!cancelled) {
          setState({
            ready: true,
            enabled:
              experiment.variant(EMAIL_PASSWORD_AUTH_FLAG)?.value === "on",
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};

export class SaasFlagsService implements IFlagsService {
  FlagsProvider = FlagsProvider;
  useFlags = useFlags;
  useEmailPasswordAuth = useEmailPasswordAuth;
}
